import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  useDroppable,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import type { DashboardTaskItem, TaskStatus } from '@bpd/shared';
import { projectsApi } from '../../api/projects.ts';
import { TaskListDnd } from '../../components/projects/TaskListDnd.tsx';
import { SearchWidget } from '../../components/search/SearchWidget.tsx';

type LoadState = 'loading' | 'ready' | 'error' | 'not-configured';
type Bucket = 'past' | 'today' | 'week' | 'month' | 'future';

const STATUS_LABEL: Record<TaskStatus, string> = {
  'not-started': 'Not started',
  'in-flight': 'In flight',
  complete: 'Complete',
};

const STATUS_BADGE_CLS: Record<TaskStatus, string> = {
  'not-started': 'bg-gray-100 text-gray-600',
  'in-flight': 'bg-phoenix-blue-100 text-phoenix-blue-700',
  complete: 'bg-green-100 text-green-800',
};

const PAST_BUCKET: { key: Bucket; label: string } = { key: 'past', label: 'Past' };
const BUCKETS: { key: Bucket; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'future', label: 'Future' },
];
const ALL_BUCKETS: { key: Bucket; label: string }[] = [PAST_BUCKET, ...BUCKETS];
const DEFAULT_EXPANDED: Bucket[] = ['today', 'week'];

function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Mirrors SearchWidget's own Tasks-group matching (item/description/clientLabel/projectName/
 * parentTaskLabel) so the same query that drives its type-ahead dropdown also determines which
 * cards remain visible below — see SearchWidget's onQueryChange. */
function matchesSearch(query: string, item: DashboardTaskItem): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [item.item, item.description, item.clientLabel, item.projectName, item.parentTaskLabel].some(
    (f) => f && f.toLowerCase().includes(q),
  );
}

/** Rolling-window bucketing throughout — deliberately not calendar-week/calendar-month semantics.
 * A calendar-month "month" bucket breaks near month-end: e.g. on July 28, the week window already
 * runs through Aug 3, which is *past* July 31, so no date could ever satisfy "after this week, but
 * still in the calendar month" — the month bucket would sit permanently empty and a drag into it
 * would silently re-land in "week" on the next render. Using a rolling 30-day-from-today window
 * for "month" instead keeps all three windows (today/week/month) contiguous and non-empty
 * regardless of where in the calendar month "today" falls. "Today" intentionally also catches
 * anything overdue-but-not-complete (date in the past); there's no separate overdue bucket for
 * in-flight/not-started work. A *complete* item with a past date is "Past" instead — it's done,
 * not something still needing attention today — and Past is filtered out entirely unless the
 * "Show completed" toggle is on (see DashboardPage's `showCompleted` state). Undated items land
 * in Future. */
function bucketFor(dateStr: string | undefined, status: TaskStatus, today: Date): Bucket {
  if (!dateStr) return 'future';
  const d = startOfDay(new Date(`${dateStr}T00:00:00`));
  if (Number.isNaN(d.getTime())) return 'future';
  if (d.getTime() <= today.getTime()) return status === 'complete' ? 'past' : 'today';
  const weekEnd = new Date(today);
  weekEnd.setDate(weekEnd.getDate() + 6);
  if (d.getTime() <= weekEnd.getTime()) return 'week';
  const monthEnd = new Date(today);
  monthEnd.setDate(monthEnd.getDate() + 30);
  if (d.getTime() <= monthEnd.getTime()) return 'month';
  return 'future';
}

/** The date a task is given when it's dragged into a bucket it doesn't already belong to — chosen
 * so it lands in that same bucket on the next render, using the exact boundary dates bucketFor
 * already computes. Dragging into Future clears the date entirely, matching "undated -> Future". */
function defaultDateForBucket(bucket: Bucket, today: Date): string | undefined {
  switch (bucket) {
    case 'past': {
      const d = new Date(today);
      d.setDate(d.getDate() - 1);
      return isoDate(d);
    }
    case 'today':
      return isoDate(today);
    case 'week': {
      const d = new Date(today);
      d.setDate(d.getDate() + 6);
      return isoDate(d);
    }
    case 'month': {
      const d = new Date(today);
      d.setDate(d.getDate() + 30);
      return isoDate(d);
    }
    case 'future':
      return undefined;
  }
}

/** Flags items whose date contradicts the dashboardOrder rank among the given list — e.g. an item
 * dragged above another but dated *after* it. Walks the list in rank order tracking the latest date
 * seen so far; anything dated earlier than that running max is flagged (the running max itself is
 * left unadvanced by a flagged item, so a single early outlier doesn't cause every later item to
 * also flag). Undated items don't participate — they have no date to conflict with, and don't
 * advance the running max either. This never changes order or date, purely a visual "worth
 * reconciling" signal. */
function computeDateConflicts(list: DashboardTaskItem[]): Set<string> {
  const flagged = new Set<string>();
  let runningMaxDate: string | null = null;
  for (const item of list) {
    if (!item.date) continue;
    if (runningMaxDate !== null && item.date < runningMaxDate) {
      flagged.add(item.id);
    } else {
      runningMaxDate = item.date;
    }
  }
  return flagged;
}

/** Runs computeDateConflicts scoped to each project separately, rather than across the whole
 * bucket — two unrelated projects landing next to each other in the master cross-project list
 * (e.g. project A's item ranked above project B's, dated later) isn't a conflict worth flagging;
 * only a project's own items being out of date-order relative to each other is. `list` is already
 * dashboardOrder-sorted, and filtering by projectId preserves that relative order within each
 * project's subsequence. */
function computeDateConflictsByProject(list: DashboardTaskItem[]): Set<string> {
  const byProject = new Map<string, DashboardTaskItem[]>();
  for (const item of list) {
    const group = byProject.get(item.projectId);
    if (group) group.push(item);
    else byProject.set(item.projectId, [item]);
  }
  const flagged = new Set<string>();
  for (const group of byProject.values()) {
    for (const id of computeDateConflicts(group)) flagged.add(id);
  }
  return flagged;
}

/** Sets or clears an item's `date` without ever assigning `undefined` to an optional property
 * (this repo's `exactOptionalPropertyTypes` forbids that — the property must be omitted instead). */
function withDate(item: DashboardTaskItem, date: string | undefined): DashboardTaskItem {
  if (date === undefined) {
    const { date: _current, ...rest } = item;
    return rest;
  }
  return { ...item, date };
}

interface DashboardRowProps {
  item: DashboardTaskItem;
  index: number;
  dragHandleProps: Record<string, unknown>;
  isSelected: boolean;
  hasDateConflict: boolean;
  onRowClick: (e: React.MouseEvent, item: DashboardTaskItem) => void;
}

function Sep() {
  return (
    <span aria-hidden="true" className="text-gray-300">
      |
    </span>
  );
}

/** A single flex-wrap row of every field, in display order, separated by `|`. Relying on
 * flex-wrap (rather than a fixed two/three-line layout) means the card collapses to one row
 * whenever the container is wide enough to fit it, and wraps field-by-field onto additional
 * lines only when it isn't — no JS/media-query breakpoint logic needed. */
function DashboardRow({
  item,
  index,
  dragHandleProps,
  isSelected,
  hasDateConflict,
  onRowClick,
}: DashboardRowProps) {
  return (
    <div
      className={`flex items-start gap-2 rounded-lg border p-3 shadow-sm transition hover:shadow-md ${
        isSelected
          ? 'border-phoenix-blue-400 bg-phoenix-blue-50'
          : 'border-gray-200 bg-white hover:border-phoenix-blue-300'
      }`}
    >
      <div
        {...dragHandleProps}
        className="mt-0.5 shrink-0 cursor-grab touch-none select-none text-gray-300 hover:text-gray-400"
      >
        ⠿
      </div>
      <Link
        to={`/admin/projects/clients/${item.clientId}/${item.projectId}`}
        state={{ from: 'dashboard' }}
        onClick={(e) => onRowClick(e, item)}
        className="flex min-w-0 flex-1 select-none flex-wrap items-center gap-x-2 gap-y-1 text-sm"
      >
        <span className="font-medium text-gray-400">{index + 1}.</span>
        <span className="text-gray-500">{item.clientLabel}</span>
        <Sep />
        <span className="text-gray-500">{item.projectName}</span>
        {item.parentTaskLabel && (
          <>
            <Sep />
            <span className="text-gray-500">under: {item.parentTaskLabel}</span>
          </>
        )}
        {item.date && (
          <>
            <Sep />
            <span className="text-gray-500">{item.date}</span>
          </>
        )}
        {hasDateConflict && (
          <>
            <Sep />
            <span
              className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
              title="This date is earlier than another item from the same project ranked above it in this bucket — order and date disagree. Reorder or re-date to reconcile."
            >
              ⚠ Out of date order
            </span>
          </>
        )}
        <Sep />
        <span className="font-semibold text-gray-900">{item.item}</span>
        <Sep />
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE_CLS[item.status]}`}
        >
          {STATUS_LABEL[item.status]}
        </span>
        {item.description && (
          <>
            <Sep />
            <span className="text-gray-600">{item.description}</span>
          </>
        )}
      </Link>
    </div>
  );
}

interface BucketHeaderProps {
  bucketKey: Bucket;
  label: string;
  count: number;
  isExpanded: boolean;
  onToggle: () => void;
}

/** The header is its own droppable target (distinct id from the bucket's item list) so a collapsed
 * bucket — "This month" is collapsed by default — can still be dragged onto: handleDragOver
 * auto-expands it on hover (mirroring ProjectDetailPage's collapsed-task auto-expand), and
 * handleDragEnd treats a drop directly on the header as "append to this bucket". */
function BucketHeader({ bucketKey, label, count, isExpanded, onToggle }: BucketHeaderProps) {
  const { setNodeRef, isOver } = useDroppable({ id: `bucket-header:${bucketKey}` });
  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onToggle}
      className={`flex w-full items-center gap-2 rounded-t-md border-b pb-2 text-left ${
        isOver ? 'border-phoenix-blue-400 bg-phoenix-blue-50' : 'border-gray-200'
      }`}
    >
      <span className="text-xs text-phoenix-blue-500">{isExpanded ? '▼' : '▶'}</span>
      <h2 className="font-serif text-lg font-semibold text-phoenix-blue-900">{label}</h2>
      <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-500">{count}</span>
    </button>
  );
}

export function DashboardPage() {
  const [state, setState] = useState<LoadState>('loading');
  const [items, setItems] = useState<DashboardTaskItem[]>([]);
  const [expanded, setExpanded] = useState<Set<Bucket>>(new Set(DEFAULT_EXPANDED));
  const [clientFilter, setClientFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [activeDragItem, setActiveDragItem] = useState<DashboardTaskItem | null>(null);
  const [dragGroupSize, setDragGroupSize] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionAnchorId, setSelectionAnchorId] = useState<string | null>(null);
  /** Surfaces a failed drag persist (date change or dashboardOrder save) instead of leaving it as a
   * console.error only — a silent failure here looks identical to success until the next reload
   * reverts it, which cost real debugging time before this existed. */
  const [reorderError, setReorderError] = useState<string | null>(null);
  const lastItemOverIdRef = useRef<string | null>(null);
  /** Snapshot of `selectedIds` taken at drag-start, so the drop handler moves the whole shift-selected
   * block together rather than just the single card dnd-kit thinks is being dragged. Null means "this
   * is an ordinary single-card drag" (either nothing was selected, or the user grabbed a card outside
   * the current selection, which resets the selection to just that card — see handleDragStart). */
  const groupDragIdsRef = useRef<Set<string> | null>(null);
  /** Every OTHER row's bounding rect, captured the instant a drag starts — before dnd-kit's own
   * sortable-strategy CSS transition begins nudging them out of the way. Reading their rects live
   * at drop time instead (via a fresh getBoundingClientRect) samples them mid-transition, which is
   * a moving target: the drop-position comparison against the dragged card's real-time position
   * came out wrong in either direction depending on exactly how far each neighbor's animation had
   * progressed at the moment the mouse was released. These snapshots aren't affected by that
   * animation since they're taken before it starts, so the comparison is against fixed geometry
   * instead of whatever frame the transition happened to be on. */
  const rowRectsAtDragStartRef = useRef<Map<string, DOMRect>>(new Map());

  function clearSelection() {
    setSelectedIds(new Set());
    setSelectionAnchorId(null);
  }

  useEffect(() => {
    if (!projectsApi.isConfigured()) {
      setState('not-configured');
      return;
    }
    setState('loading');
    projectsApi
      .listDashboardItems()
      .then((data) => {
        setItems(data);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, []);

  useEffect(() => {
    clearSelection();
  }, [clientFilter, projectFilter, searchQuery]);

  function toggleBucket(bucket: Bucket) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(bucket)) next.delete(bucket);
      else next.add(bucket);
      return next;
    });
  }

  const clients = useMemo(
    () =>
      Array.from(new Map(items.map((i) => [i.clientId, i.clientLabel])).entries()).sort((a, b) =>
        a[1].localeCompare(b[1]),
      ),
    [items],
  );
  const projects = useMemo(
    () =>
      Array.from(
        new Map(
          items
            .filter((i) => !clientFilter || i.clientId === clientFilter)
            .map((i) => [i.projectId, i.projectName]),
        ).entries(),
      ).sort((a, b) => a[1].localeCompare(b[1])),
    [items, clientFilter],
  );

  const filtered = items.filter(
    (i) =>
      (!clientFilter || i.clientId === clientFilter) &&
      (!projectFilter || i.projectId === projectFilter) &&
      matchesSearch(searchQuery, i),
  );

  const today = startOfDay(new Date());
  const byBucket = new Map<Bucket, DashboardTaskItem[]>(ALL_BUCKETS.map((b) => [b.key, []]));
  for (const item of filtered) {
    const bucket = bucketFor(item.date, item.status, today);
    if (bucket === 'past' && !showCompleted) continue; // hidden entirely unless the toggle is on
    byBucket.get(bucket)!.push(item);
  }
  // `date` already decides WHICH bucket an item lands in (see bucketFor) — it must not also drive
  // the sort WITHIN a bucket, or drag-to-reorder can never win: two items with different real
  // dates (e.g. one item due 2026-09-14 next to an undated one, both >30 days out and so both in
  // Future) would always resolve by date first, making any dashboardOrder the drag persists
  // irrelevant whenever the dates differ. dashboardOrder is the only ordering axis here now.
  const dateConflicts = new Map<Bucket, Set<string>>();
  for (const [bucket, list] of byBucket.entries()) {
    list.sort((a, b) => (a.dashboardOrder ?? 0) - (b.dashboardOrder ?? 0));
    dateConflicts.set(bucket, computeDateConflictsByProject(list));
  }
  const visibleBuckets = showCompleted ? ALL_BUCKETS : BUCKETS;

  function bucketContaining(id: string): Bucket | null {
    for (const b of ALL_BUCKETS) {
      if ((byBucket.get(b.key) ?? []).some((i) => i.id === id)) return b.key;
    }
    return null;
  }

  /** Shift-click range-select, scoped to the clicked card's own bucket (a group drag can't cross
   * buckets — see handleDragEnd — so a selection spanning buckets could never be moved as one unit).
   * A plain click always clears any existing selection and falls through to the card's normal
   * navigation. The first shift-click of a run sets both the anchor and the selection to just that
   * card; every subsequent shift-click re-selects the full range between the anchor and the new
   * card, same as Finder/Explorer. Shift-clicking into a different bucket re-anchors there instead
   * of trying to extend a range that can't include it. */
  function handleRowClick(e: React.MouseEvent, item: DashboardTaskItem, bucketKey: Bucket) {
    if (!e.shiftKey) {
      if (selectedIds.size > 0) clearSelection();
      return;
    }
    e.preventDefault();
    const list = byBucket.get(bucketKey) ?? [];
    const anchorId =
      selectionAnchorId && list.some((i) => i.id === selectionAnchorId)
        ? selectionAnchorId
        : item.id;
    const anchorIndex = list.findIndex((i) => i.id === anchorId);
    const clickedIndex = list.findIndex((i) => i.id === item.id);
    if (anchorIndex === -1 || clickedIndex === -1) return;
    const [start, end] =
      anchorIndex <= clickedIndex ? [anchorIndex, clickedIndex] : [clickedIndex, anchorIndex];
    setSelectedIds(new Set(list.slice(start, end + 1).map((i) => i.id)));
    setSelectionAnchorId(anchorId);
  }

  function handleDragStart(event: DragStartEvent) {
    lastItemOverIdRef.current = null;
    const id = String(event.active.id);
    const item = items.find((i) => i.id === id) ?? null;
    setActiveDragItem(item);
    if (item && selectedIds.has(id) && selectedIds.size > 1) {
      groupDragIdsRef.current = new Set(selectedIds);
      setDragGroupSize(selectedIds.size);
    } else {
      groupDragIdsRef.current = null;
      setDragGroupSize(null);
      if (selectedIds.size > 0) clearSelection();
    }
    const rects = new Map<string, DOMRect>();
    document.querySelectorAll('[data-row-id]').forEach((el) => {
      const rowId = el.getAttribute('data-row-id');
      if (rowId) rects.set(rowId, el.getBoundingClientRect());
    });
    rowRectsAtDragStartRef.current = rects;
  }

  function handleDragOver(event: DragOverEvent) {
    const { over } = event;
    if (!over) return;
    const overId = String(over.id);
    if (overId.startsWith('bucket-header:')) {
      const bucket = overId.slice('bucket-header:'.length) as Bucket;
      setExpanded((prev) => (prev.has(bucket) ? prev : new Set(prev).add(bucket)));
      return;
    }
    if (!overId.startsWith('bucket:')) {
      lastItemOverIdRef.current = overId;
    }
  }

  /** Assigns fresh `dashboardOrder` values to the moved item(s) only — every other item in
   * `newDestOrder` keeps its existing value untouched. `movedIds` is always exactly one contiguous
   * run within `newDestOrder` (a single dragged card, or a shift-selected block — see
   * handleRowClick), so the new values just need to subdivide the gap between whatever sits
   * immediately before and after that run once the move is applied. Fractional (not integer) so a
   * drop can slot in between any two neighbors without ever renumbering the rest of the bucket. */
  function assignInterpolatedOrders(
    newDestOrder: DashboardTaskItem[],
    movedIds: Set<string>,
  ): Map<string, number> {
    const startIdx = newDestOrder.findIndex((i) => movedIds.has(i.id));
    if (startIdx === -1) return new Map();
    let endIdx = startIdx;
    while (endIdx + 1 < newDestOrder.length && movedIds.has(newDestOrder[endIdx + 1]!.id)) endIdx++;
    const movedCount = endIdx - startIdx + 1;

    const prevItem = startIdx > 0 ? newDestOrder[startIdx - 1] : null;
    const nextItem = endIdx < newDestOrder.length - 1 ? newDestOrder[endIdx + 1] : null;
    const nextOrderFallback = (nextItem?.dashboardOrder ?? 0) - (movedCount + 1);
    const prevOrder = prevItem?.dashboardOrder ?? nextOrderFallback;
    const prevOrderFallback = prevOrder + movedCount + 1;
    const nextOrder = nextItem?.dashboardOrder ?? prevOrderFallback;
    const step = (nextOrder - prevOrder) / (movedCount + 1);

    const result = new Map<string, number>();
    for (let k = 0; k < movedCount; k++) {
      result.set(newDestOrder[startIdx + k]!.id, prevOrder + step * (k + 1));
    }
    return result;
  }

  /** Persists a dashboard drag: an optional date change (for every item that crossed into a
   * different bucket — a group drag moves every selected item, not just the one dnd-kit reports as
   * `active`) plus a dashboardOrder update for whichever item(s) actually moved — dashboardOrder is
   * comparable across every project/client (unlike `sequence`, which is scoped per-parent), so this
   * is the one field that can express "this block now sits between these two neighbors" regardless
   * of which project each side belongs to. */
  async function applyReorder(
    movedItems: DashboardTaskItem[],
    sourceBucket: Bucket,
    targetBucket: Bucket,
    movedIds: Set<string>,
    newDestOrder: DashboardTaskItem[],
  ) {
    setReorderError(null);

    if (targetBucket !== sourceBucket) {
      const newDate = defaultDateForBucket(targetBucket, today);
      setItems((prev) => prev.map((i) => (movedIds.has(i.id) ? withDate(i, newDate) : i)));
      try {
        await Promise.all(
          movedItems.map((i) =>
            projectsApi.updateItem(i.id, i.parentType, i.parentId, { date: newDate ?? null }),
          ),
        );
      } catch (e) {
        console.error('Failed to move task(s) to a new date bucket', e);
        setReorderError("Couldn't save the date change — it may revert on reload. Try again?");
      }
    }

    const newOrders = assignInterpolatedOrders(newDestOrder, movedIds);
    if (newOrders.size === 0) return;

    setItems((prev) =>
      prev.map((i) => {
        const order = newOrders.get(i.id);
        return order === undefined ? i : { ...i, dashboardOrder: order };
      }),
    );
    try {
      await projectsApi.reorderDashboard(
        newDestOrder
          .filter((i) => newOrders.has(i.id))
          .map((i) => ({
            id: i.id,
            parentType: i.parentType,
            parentId: i.parentId,
            dashboardOrder: newOrders.get(i.id)!,
          })),
      );
    } catch (e) {
      setReorderError("Couldn't save the new order — it will revert on reload. Try again?");
      console.error('Failed to persist dashboard reorder', e);
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const dragged = activeDragItem;
    const groupIds = groupDragIdsRef.current;
    groupDragIdsRef.current = null;
    setActiveDragItem(null);
    setDragGroupSize(null);
    const { over, active } = event;
    if (!over || !dragged) return;
    const overId = String(over.id);

    const sourceBucket = bucketFor(dragged.date, dragged.status, today);
    let targetBucket: Bucket | null = null;
    if (overId.startsWith('bucket-header:')) {
      targetBucket = overId.slice('bucket-header:'.length) as Bucket;
    } else if (overId.startsWith('bucket:')) {
      targetBucket = overId.slice('bucket:'.length) as Bucket;
    } else {
      targetBucket = bucketContaining(overId);
      if (!targetBucket && lastItemOverIdRef.current) {
        targetBucket = bucketContaining(lastItemOverIdRef.current);
      }
    }
    if (!targetBucket) return;
    // A group drop into a different bucket re-dates every selected item to that bucket's default
    // date (same rule a single-item cross-bucket drag already uses) — there's no single "right"
    // date per item, but landing the whole block in the target bucket is the obviously-intended
    // outcome of dragging a multi-select onto it, so this no longer silently no-ops.

    const movedIds = groupIds ?? new Set([dragged.id]);
    const destList = byBucket.get(targetBucket) ?? [];
    // `destList` already contains the dragged item(s) for a same-bucket reorder, but NOT for a
    // cross-bucket move (it's still filed under its old bucket in this render) — filtering movedIds
    // out either way leaves exactly "every OTHER item currently in the target bucket", uniformly.
    const remaining = destList.filter((i) => !movedIds.has(i.id));
    const movedItemsInOrder = groupIds ? destList.filter((i) => groupIds.has(i.id)) : [dragged];

    // `overId` (dnd-kit's collision winner) is very often one of the OTHER moved cards rather than
    // a `remaining` one — a shift-selected block is contiguous by construction (see
    // handleRowClick), so whatever the pointer is nearest to mid-drag is usually another moved
    // card, or the shared bucket container itself in the gap between cards. Resolving the drop
    // position from `overId`/`lastItemOverIdRef` alone therefore misses constantly, silently
    // no-oping the drag (or landing at a fixed index instead of where it was actually dropped)
    // regardless of where the user released the mouse. Instead, read the dragged card's actual
    // on-screen position at drop time (dnd-kit exposes it via `active.rect`) and compare it
    // against each `remaining` card's PRE-DRAG rect snapshot (see rowRectsAtDragStartRef) — NOT a
    // fresh getBoundingClientRect() here, which would sample those cards mid-CSS-transition
    // (dnd-kit's own sortable strategy nudges them out of the way as the drag passes over them),
    // making the comparison against a moving target and landing wrong in either direction
    // depending on exactly how far that animation had progressed at drop time.
    const activeRect = active.rect.current.translated ?? active.rect.current.initial;
    const activeCenter = activeRect ? activeRect.top + activeRect.height / 2 : null;
    let insertIndex = remaining.length;
    if (activeCenter !== null) {
      const foundIndex = remaining.findIndex((item) => {
        const rect = rowRectsAtDragStartRef.current.get(item.id);
        if (!rect) return false;
        return activeCenter < rect.top + rect.height / 2;
      });
      if (foundIndex !== -1) insertIndex = foundIndex;
    }
    const newDestOrder = [
      ...remaining.slice(0, insertIndex),
      ...movedItemsInOrder,
      ...remaining.slice(insertIndex),
    ];
    void applyReorder(movedItemsInOrder, sourceBucket, targetBucket, movedIds, newDestOrder);
    if (groupIds) clearSelection();
  }

  return (
    <main className="mx-auto max-w-5xl px-6 py-16">
      <h1 className="font-serif text-3xl font-bold text-phoenix-blue-900">Dashboard</h1>
      <p className="mt-2 text-sm text-gray-500">
        Everything to do, across every client and project, grouped by when it&apos;s due. Drag to
        reorder, or drag into a different bucket to change its date.
      </p>

      <div className="mt-4">
        <SearchWidget categories={['tasks']} onQueryChange={setSearchQuery} />
      </div>

      {state === 'not-configured' && (
        <p className="mt-8 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          The project management backend isn&apos;t connected yet in this environment.
        </p>
      )}
      {state === 'error' && (
        <p className="mt-8 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Couldn&apos;t load the dashboard. Try again in a moment.
        </p>
      )}
      {state === 'loading' && <p className="mt-8 text-sm text-gray-500">Loading…</p>}

      {state === 'ready' && (
        <>
          <div className="mt-6 flex flex-wrap gap-3">
            <select
              value={clientFilter}
              onChange={(e) => {
                setClientFilter(e.target.value);
                setProjectFilter('');
              }}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            >
              <option value="">All clients</option>
              {clients.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            >
              <option value="">All projects</option>
              {projects.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
                className="rounded border-gray-300 text-phoenix-blue-600 focus:ring-phoenix-blue-500"
              />
              Show completed
            </label>
          </div>

          {reorderError && (
            <div className="mt-3 flex items-center gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <span>{reorderError}</span>
              <button
                type="button"
                onClick={() => setReorderError(null)}
                className="ml-auto text-red-600 hover:underline"
              >
                Dismiss
              </button>
            </div>
          )}

          {selectedIds.size > 0 && (
            <div className="mt-3 flex items-center gap-3 rounded-md border border-phoenix-blue-200 bg-phoenix-blue-50 px-3 py-2 text-sm text-phoenix-blue-800">
              <span className="font-medium">{selectedIds.size} selected</span>
              <span className="text-phoenix-blue-300">·</span>
              <span>
                Drag any selected card to move the group, or shift-click to adjust the selection.
              </span>
              <button
                type="button"
                onClick={clearSelection}
                className="ml-auto text-phoenix-blue-600 hover:underline"
              >
                Clear
              </button>
            </div>
          )}

          <DndContext
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div className="mt-6 space-y-6">
              {visibleBuckets.map(({ key, label }) => {
                const list = byBucket.get(key) ?? [];
                // While a search is active, a bucket holding matches forces itself open regardless
                // of its manually-toggled collapsed/expanded state — otherwise a match landing in a
                // bucket collapsed by default (e.g. "This month") is invisible unless the user
                // happens to expand it themselves first. Reverts to the plain manual state once the
                // search is cleared.
                const isExpanded = expanded.has(key) || (searchQuery.trim() !== '' && list.length > 0);
                return (
                  <div key={key}>
                    <BucketHeader
                      bucketKey={key}
                      label={label}
                      count={list.length}
                      isExpanded={isExpanded}
                      onToggle={() => toggleBucket(key)}
                    />
                    {isExpanded && (
                      <div className="mt-3">
                        {list.length === 0 && (
                          <p className="py-1 text-sm text-gray-400">
                            {searchQuery.trim() ? 'No matches in this bucket.' : 'Nothing here.'}
                          </p>
                        )}
                        <TaskListDnd
                          containerId={`bucket:${key}`}
                          items={list}
                          renderItem={(item, dragHandleProps) => (
                            <DashboardRow
                              key={item.id}
                              item={item}
                              index={list.findIndex((i) => i.id === item.id)}
                              dragHandleProps={dragHandleProps}
                              isSelected={selectedIds.has(item.id)}
                              hasDateConflict={dateConflicts.get(key)?.has(item.id) ?? false}
                              onRowClick={(e, clicked) => handleRowClick(e, clicked, key)}
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <DragOverlay>
              {activeDragItem && (
                <div className="rounded-lg border border-phoenix-blue-300 bg-white p-3 shadow-lg">
                  <p className="text-sm font-semibold text-gray-900">
                    {dragGroupSize ? `Moving ${dragGroupSize} items` : activeDragItem.item}
                  </p>
                </div>
              )}
            </DragOverlay>
          </DndContext>
        </>
      )}
    </main>
  );
}
